import {path, invoker} from 'ramda';

import {GetPrefetchOffersResponse} from 'components/app/AdobeTargetProvider/types';
import {
  EnsightenDataLayer,
  Claims,
  Policy,
  Profile,
  Quote,
  Global,
  Message,
  EnsightenLogItem,
  Billing,
  ABTests
} from 'shared/Ensighten/types';
import {NamedPageDataTemplate, getInitialState} from 'shared/Ensighten/utils';

/**
 * Handles all functions for ensighten tracking, including setting and manipulating the ensighten data layer
 */
class Ensighten {
  dataLayer: EnsightenDataLayer;

  constructor() {
    this.dataLayer = getInitialState();
    this.persistDataLayer();
  }

  /**
   * Sets one or more `visitor` dataLayer keys
   */
  setClaimsData = (data: Claims) => {
    const claimsData: Claims = {
      ...this.dataLayer.claims,
      ...data
    };

    this.dataLayer.claims = claimsData;

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  setPolicyData = (data: Policy) => {
    const policyData: Policy = {
      ...this.dataLayer.policy,
      ...data
    };

    this.dataLayer.policy = policyData;

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  setProfileData = (data: Profile) => {
    const profileData: Profile = {
      ...this.dataLayer.profile,
      ...data
    };

    this.dataLayer.profile = profileData;

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  setQuoteData = (data: Quote) => {
    const quoteData: Quote = {
      ...this.dataLayer.quote,
      ...data
    };

    this.dataLayer.quote = quoteData;

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  setBillingData = (data: Billing) => {
    const billingData: Billing = {
      ...this.dataLayer.billing,
      ...data
    };

    this.dataLayer.billing = billingData;

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  setABTestData = (data: GetPrefetchOffersResponse['prefetch']['mboxes'][0]) => {
    const {activeTests} = this.dataLayer.abTests;

    const activeTestSet = new Set(activeTests);
    if (data.options?.length) {
      activeTestSet.add(`${data.name}_${data.options[0]?.content.experienceGroup}`);
    }

    const abTestsData: ABTests = {
      ...this.dataLayer.abTests,
      activeTests: Array.from(activeTestSet)
    };

    this.dataLayer.abTests = abTestsData;
    try {
      this.persistDataLayer();
    } catch (e) {}
  };
  // should be used in case where we want to track data not associated with any specific page or button
  ensightenDataEvents = (event: string, data?: any) => {
    const ensDataTrigger = (window as any).getEnsightenDataObject;
    try {
      ensDataTrigger(event, data);
    } catch (exception) {}
  };

  setMitigationType = (activity: string) => {
    const globalData: Global = {
      ...this.dataLayer.global,
      mitigationType: activity
    };

    this.setGlobalData(globalData);

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  setGlobalData = (globalData: Global) => {
    this.dataLayer.global = globalData;

    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  /**
   * Get visitor data by key (or all data fields)
   *
   * @return - Either single attribute or all attributes
   */
  getClaimsData(key: string): string | undefined;
  getClaimsData(): Global;
  getClaimsData(key?: string) {
    if (key) {
      return path(['claims', key], this.dataLayer);
    }
    return this.dataLayer.claims;
  }

  getBillingData(key: string): string | undefined;
  getBillingData(): Global;
  getBillingData(key?: string) {
    if (key) {
      return path(['billing', key], this.dataLayer);
    }
    return this.dataLayer.billing;
  }

  getPolicyData(key: string): string | undefined;
  getPolicyData(): Global;
  getPolicyData(key?: string) {
    if (key) {
      return path(['policy', key], this.dataLayer);
    }
    return this.dataLayer.policy;
  }

  getProfileData(key: string): string | undefined;
  getProfileData(): Global;
  getProfileData(key?: string) {
    if (key) {
      return path(['profile', key], this.dataLayer);
    }
    return this.dataLayer.profile;
  }

  getQuoteData(key: string): string | undefined;
  getQuoteData(): Global;
  getQuoteData(key?: string) {
    if (key) {
      return path(['quote', key], this.dataLayer);
    }
    return this.dataLayer.quote;
  }

  /**
   * Get global data by key (or all data fields)
   *
   * @return - Either single attribute or all attributes
   */
  getGlobalData(key: string): string | undefined;
  getGlobalData(): Global;
  getGlobalData(key?: string) {
    if (key) {
      return path(['global', key], this.dataLayer);
    }
    return this.dataLayer.global;
  }

  ensightenCustomEvent = (name: string, type: string) => {
    const ensTargetCustomEventTrigger = (window as any).TrackTargetEvent;
    try {
      ensTargetCustomEventTrigger(name, type);
    } catch (exception) {}
  };

  ensightenLogger = (data: EnsightenLogItem) => {
    const ensLogTrigger = (window as any).EnsLogger;
    const {log} = this.dataLayer;
    log.logInfo = data.logInfo;
    try {
      this.persistDataLayer();
      ensLogTrigger();
      this.clearEnsightenLogger();
    } catch (exception) {}
  };
  /**
   * Adds message to data layer
   *
   * @param m - message to be added
   */
  addMessage = (m: Message) => {
    const {message} = this.dataLayer;
    message.messageID = m.messageID;
    message.messageType = m.messageType;
    message.messageDescription = m.messageDescription;
    try {
      this.persistDataLayer();
      // invoker(window, 'messageTrigger');
      // This is a function that is defined in the bootstrapper file that analytics
      // set up.
      invoker(0, 'messageTrigger')(window as any);
      this.clearMessages();
    } catch (exception) {}
  };

  addErrorMessage = (m: string) => {
    this.addMessage({
      messageID: 'Message',
      messageType: 'Error',
      messageDescription: m
    });
  };

  addValidationError = (m: string) => {
    this.addMessage({
      messageID: 'Error',
      messageType: 'Validation',
      messageDescription: m
    });
  };

  /**
   * Clears all messages from data layer
   */
  clearMessages = () => {
    const {message} = this.dataLayer;
    message.messageID = '';
    message.messageType = '';
    message.messageDescription = '';
    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  clearEnsightenLogger = () => {
    const {log} = this.dataLayer;
    log.logInfo = '';
    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  /**
   * Clears all visitor data from data layer
   */
  clearVisitorData = () => {
    this.dataLayer.claims = getInitialState().claims;
    this.dataLayer.policy = getInitialState().policy;
    this.dataLayer.profile = getInitialState().profile;
    this.dataLayer.quote = getInitialState().quote;
    this.dataLayer.billing = getInitialState().billing;
    this.dataLayer.abTests = getInitialState().abTests;
    try {
      this.persistDataLayer();
    } catch (e) {}
  };

  /**
   * Called each time a new component is loaded, sends page trigger to ensighten
   *
   * @param pageData - data for current page
   */
  triggerPageLoad = (pageData: NamedPageDataTemplate) => {
    const globalObj: Global = this.dataLayer.global;
    globalObj.pageCategory = pageData.category;
    globalObj.pageName = pageData.name;
    globalObj.timeStamp = +new Date();

    try {
      this.persistDataLayer();
      invoker(0, 'pageLoadTrigger')(window as any);
    } catch (e) {}
  };

  /**
   * Store the current dataLayer in sessionStorage
   */
  persistDataLayer = () => {
    sessionStorage.setItem('dataLayer', JSON.stringify(this.dataLayer));
  };
}

export const ensighten = new Ensighten();
